import { useCallback, useEffect, useRef, useState } from "react";

// The attribute a section carries to appear on the rail. Its value is the id
// the page gave the section.
export const RAIL_SECTION_ATTRIBUTE = "data-rail-section";

// A section becomes the one being read once its top passes this share of the
// scroll container's height: the reader's eyes are near the top of the page,
// not on its first pixel.
const READING_LINE = 0.3;

type RailMeasure<Id extends string> = {
  activeId: Id | null;
  // Each measured section's height over the tallest one's.
  weights: ReadonlyMap<Id, number>;
};

function sectionElement(
  container: HTMLElement,
  id: string
): HTMLElement | null {
  return container.querySelector<HTMLElement>(
    `[${RAIL_SECTION_ATTRIBUTE}="${id}"]`
  );
}

function measure<Id extends string>(
  container: HTMLElement,
  ids: Id[]
): RailMeasure<Id> {
  const elements = ids.map((id) => sectionElement(container, id));
  const heights = elements.map((element) =>
    element === null ? 0 : element.offsetHeight
  );
  const tallest = Math.max(1, ...heights);
  const readingLine =
    container.getBoundingClientRect().top +
    container.clientHeight * READING_LINE;
  // The last section whose top is above the reading line. At the very bottom
  // of the page the last section is the one being read, even when it is too
  // short to reach the line.
  const isAtBottom =
    container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
  let activeId: Id | null = null;
  for (const [index, id] of ids.entries()) {
    const element = elements[index];
    if (element === undefined || element === null) continue;
    if (element.getBoundingClientRect().top <= readingLine) activeId = id;
  }
  const lastId = ids.at(-1);
  return {
    activeId: isAtBottom && lastId !== undefined ? lastId : activeId,
    weights: new Map(
      ids.map((id, index) => {
        const height = heights[index];
        if (height === undefined) {
          throw new Error(`should not happen: no height for "${id}"`);
        }
        return [id, height / tallest];
      })
    ),
  };
}

/**
 * Where the reader is in a long page, for the SectionRail: which section is
 * being read, how tall each one is, and a way to jump to one. Reads the DOM of
 * the scroll container, where every section carries `RAIL_SECTION_ATTRIBUTE`.
 */
export function useSectionRail<Id extends string>(
  scrollRef: React.RefObject<HTMLElement | null>,
  ids: Id[]
): {
  activeId: Id | null;
  weights: ReadonlyMap<Id, number>;
  scrollTo: (id: string) => void;
} {
  const [state, setState] = useState<RailMeasure<Id>>({
    activeId: null,
    weights: new Map(),
  });
  // Re-measure when the set of sections changes, not when the caller builds a
  // new array with the same ids.
  const idsKey = ids.join("|");
  // The ids the listeners measure: always this render's, read when they fire.
  const idsRef = useRef(ids);
  idsRef.current = ids;

  useEffect(() => {
    const container = scrollRef.current;
    if (container === null) return;
    function update(): void {
      if (container === null) return;
      setState(measure(container, idsRef.current));
    }
    update();
    container.addEventListener("scroll", update, { passive: true });
    // Figures load and sections reflow after the first paint; their heights
    // are the tick lengths.
    const observer = new ResizeObserver(update);
    observer.observe(container);
    for (const child of Array.from(container.children)) {
      observer.observe(child);
    }
    return () => {
      container.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [scrollRef, idsKey]);

  const scrollTo = useCallback(
    (id: string): void => {
      const container = scrollRef.current;
      if (container === null) return;
      const element = sectionElement(container, id);
      if (element === null) {
        throw new Error(`should not happen: no rail section "${id}"`);
      }
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [scrollRef]
  );

  return { activeId: state.activeId, weights: state.weights, scrollTo };
}
